import clsx from 'clsx';
import type {
  ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, Ref,
  SelectHTMLAttributes, TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerSolid';

/**
 * Each variant carries a rest, a hover and a pressed state.
 *
 * The pressed state is the one that was missing. Without it a button reacts on hover and
 * then does nothing under the finger, which is what makes a control feel like a picture
 * of a control — the click is confirmed only when the screen behind it finally changes.
 */
const variants: Record<Variant, string> = {
  primary:
    'border-transparent bg-[var(--ff-primary)] text-[var(--ff-on-primary)] '
    + 'shadow-[var(--ff-shadow)] hover:bg-[var(--ff-primary-hover)] '
    + 'active:bg-[var(--ff-primary-hover)] active:shadow-none',
  secondary:
    'border-[var(--ff-border-strong)] bg-[var(--ff-surface)] text-[var(--ff-text)] '
    + 'hover:border-[var(--ff-text-faint)] hover:bg-[var(--ff-surface-raised)] '
    + 'active:bg-[var(--ff-surface-sunken)]',
  ghost:
    'border-transparent bg-transparent text-[var(--ff-text-muted)] '
    + 'hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)] '
    + 'active:bg-[var(--ff-surface-sunken)]',
  danger:
    'border-[var(--ff-border)] bg-transparent text-[var(--ff-danger)] '
    + 'hover:border-[var(--ff-danger)] hover:bg-[var(--ff-danger-soft)] '
    + 'active:bg-[var(--ff-danger-soft)]',

  // Filled, for the one button in a destructive confirmation that carries it out. A
  // variant rather than a className on `danger`, because overriding that one's
  // `bg-transparent` depends on which rule the stylesheet happens to emit last — which is
  // how the confirm button first shipped as white text on nothing.
  dangerSolid:
    'border-transparent bg-[var(--ff-danger)] text-white shadow-[var(--ff-shadow)] '
    + 'hover:brightness-110 active:brightness-95 active:shadow-none',
};

const sizes = {
  sm: 'min-h-8 gap-1.5 px-2.5 text-[13px]',
  md: 'min-h-9 gap-2 px-3 text-sm',
};

export function Button({
  variant = 'secondary', size = 'md', className, children, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: keyof typeof sizes;
  /** React 19 passes ref as an ordinary prop, so it rides along in rest. */
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      // 44px on touch targets, 32px minimum on dense desktop controls (WCAG 2.5.8).
      className={clsx(
        'inline-flex select-none items-center justify-center rounded-md border py-2',
        'font-medium transition-[background-color,border-color,box-shadow,color] duration-150',
        'disabled:pointer-events-none disabled:opacity-50',
        sizes[size], variants[variant], className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function IconButton({
  label, className, on, children, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  /**
   * Draws the button as switched on, for a toggle such as Required.
   *
   * A prop rather than a colour passed through className: the base already sets a text
   * colour, and an override of equal specificity is decided by which rule the stylesheet
   * emits last — so the "on" colour silently lost and the toggle looked identical either
   * way.
   */
  on?: boolean;
}) {
  return (
    <button
      aria-label={label}
      title={label}
      className={clsx(
        'inline-flex h-8 w-8 items-center justify-center rounded-md',
        'transition-colors duration-150',
        on
          ? 'bg-[var(--ff-primary)]/10 text-[var(--ff-primary)] hover:bg-[var(--ff-primary)]/15'
          : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]',
        'active:bg-[var(--ff-surface-sunken)]',
        'disabled:pointer-events-none disabled:opacity-40', className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * Fields take their focus colour from the accent rather than relying on the page-wide
 * focus outline alone: the outline says "this is focused", the border says "this is the
 * thing you are typing into", and on a dense form those are different questions.
 */
const fieldClass =
  'w-full rounded-md border border-[var(--ff-border-strong)] bg-transparent '
  + 'px-3 py-2 text-sm text-[var(--ff-text)] '
  + 'placeholder:text-[var(--ff-text-faint)] '
  + 'transition-[border-color,box-shadow] duration-150 '
  + 'hover:border-[var(--ff-text-faint)] '
  + 'focus:border-[var(--ff-primary)] '
  + 'disabled:cursor-not-allowed disabled:opacity-60 '
  + 'aria-[invalid=true]:border-[var(--ff-danger)]';

/**
 * Label is always rendered and always associated. A placeholder is never the label —
 * it disappears on focus and fails 3.3.2 (docs/17 §4).
 */
export function Field({
  label, hint, error, required, children,
}: {
  label: string; hint?: string; error?: string; required?: boolean; children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-[var(--ff-text)]">
        {label}
        {required && <span className="text-[var(--ff-danger)]" aria-hidden="true"> *</span>}
      </span>
      {hint && <span className="mb-1 block text-xs text-[var(--ff-text-muted)]">{hint}</span>}
      {children}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-[var(--ff-danger)]">{error}</span>
      )}
    </label>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx(fieldClass, 'min-h-9', className)} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={clsx(fieldClass, 'min-h-20 resize-y', className)} {...rest} />;
}

/**
 * ff-select (index.css) swaps the platform arrow for one drawn to match the icon set. The
 * native control is otherwise left alone — its popup is the operating system's, which
 * beats any list we could draw for keyboard, touch and screen readers alike.
 */
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={clsx(fieldClass, 'ff-select min-h-9', className)} {...rest}>
      {children}
    </select>
  );
}

export function Checkbox({
  label, description, checked, onChange, disabled,
}: {
  label: string; description?: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2 py-1">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-[var(--ff-primary)]"
        aria-describedby={description ? `${id}-d` : undefined}
      />
      <div className="min-w-0">
        <label htmlFor={id} className="block cursor-pointer text-sm">{label}</label>
        {description && (
          <p id={`${id}-d`} className="text-xs text-[var(--ff-text-muted)]">{description}</p>
        )}
      </div>
    </div>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={clsx(
      'rounded-lg border border-[var(--ff-border)] bg-[var(--ff-surface)]',
      'shadow-[var(--ff-shadow)]', className,
    )}>
      {children}
    </div>
  );
}

/**
 * A hairline as well as a tint.
 *
 * On the light theme the soft fills are close enough to the page that a badge read as
 * coloured text with a smudge behind it; the border is what makes it a token.
 */
export function Badge({
  tone = 'neutral', children,
}: { tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'primary'; children: ReactNode }) {
  const tones = {
    neutral: 'border-[var(--ff-border)] bg-[var(--ff-surface-sunken)] text-[var(--ff-text-muted)]',
    success: 'border-[var(--ff-success)]/30 bg-[var(--ff-success-soft)] text-[var(--ff-success)]',
    warning: 'border-[var(--ff-warning)]/30 bg-[var(--ff-warning-soft)] text-[var(--ff-warning)]',
    danger: 'border-[var(--ff-danger)]/30 bg-[var(--ff-danger-soft)] text-[var(--ff-danger)]',
    primary: 'border-[var(--ff-primary)]/30 bg-[var(--ff-primary)]/10 text-[var(--ff-primary)]',
  };
  return (
    <span className={clsx(
      'inline-flex items-center rounded-full border px-2 py-[1px]',
      'text-[11px] font-semibold leading-[1.5]',
      tones[tone],
    )}>
      {children}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-[var(--ff-text-muted)]">
      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor"
                strokeWidth="3" opacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({
  title, description, action,
}: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <h2 className="text-base font-semibold">{title}</h2>
      {description && (
        <p className="max-w-md text-sm text-[var(--ff-text-muted)]">{description}</p>
      )}
      {action}
    </div>
  );
}

/**
 * Errors are surfaced as a list with the machine code visible: the person reading this
 * is usually the integrating developer, and the code is what they will search for.
 */
export function ErrorList({
  title, issues, tone = 'danger',
}: {
  title: string;
  issues: { path?: string; code: string; message: string }[];
  tone?: 'danger' | 'warning';
}) {
  if (issues.length === 0) return null;
  const isDanger = tone === 'danger';

  return (
    <div
      role={isDanger ? 'alert' : undefined}
      className={clsx(
        'rounded-md border px-3 py-2 text-sm',
        isDanger
          ? 'border-[var(--ff-danger)] bg-[var(--ff-danger-soft)]'
          : 'border-[var(--ff-warning)] bg-[var(--ff-warning-soft)]',
      )}
    >
      <p className={clsx(
        'mb-1 font-semibold',
        isDanger ? 'text-[var(--ff-danger)]' : 'text-[var(--ff-warning)]',
      )}>
        {title}
      </p>
      {/*
        The path and code sit on their own line rather than trailing the sentence. Inline,
        a message ending in a field list ran straight into them and read as part of it:
        "…: derived, other.questions · LOGIC_CYCLE".
      */}
      <ul className="space-y-2">
        {issues.map((issue, i) => (
          <li key={`${issue.code}-${i}`} className="text-[var(--ff-text)]">
            <div className="leading-relaxed">{issue.message}</div>
            <div className="mt-0.5 font-mono text-[11px] text-[var(--ff-text-muted)]">
              {issue.path ? `${issue.path} · ` : ''}{issue.code}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[var(--ff-border)]
                    bg-[var(--ff-surface)] px-4 py-2">
      {children}
    </div>
  );
}
